import React, { useState, useRef, useEffect, useContext } from 'react'
import PropTypes from 'prop-types'
import { Backup, Delete, DeleteForever, Close, GetApp, Warning } from '@material-ui/icons'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  makeStyles,
} from '@material-ui/core'
import { getAWSCredentials, putObjectToS3 } from '../service/aws'
import { fileToBuffer } from '../utils/fileToBuffer'
import { deleteBookingAttachment, getBookingAttachmentByType, getBookingAttachmentDownloadLink } from '../service/booking'
import { useTranslation } from 'react-i18next'
import { Alert } from '@material-ui/lab'
import { Context } from './../Store'

const receiptAttachmentType = 'booking-prepayment-receipt'

const useStyles = makeStyles((theme) => ({
  uploadContainer: {
    position: 'relative',
    borderStyle: 'dashed',
    borderWidth: '0.15rem',
    borderRadius: '0.5rem',
    padding: '1rem',
  },
  uploadProgress: {
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    backgroundImage: 'linear-gradient(to right, #05a8a430, #06a8a530)',
  },
  inputContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '1rem',
  },
  iconCursor: {
    cursor: 'pointer',
    transition: 'color 0.3s',
  },
  iconActionPrimary: {
    '&:hover': {
      color: theme.palette.primary.main,
    },
    '&:active': {
      color: theme.palette.primary.dark,
    },
  },
  iconActionDanger: {
    '&:hover': {
      color: theme.palette.error.main,
    },
    '&:active': {
      color: theme.palette.error.dark,
    },
  },
}))

const UploadReceipt = ({ booking_id, setError, clearErrors }) => {
  const [state, dispatch] = useContext(Context)
  const classes = useStyles()
  const { t } = useTranslation('translations')
  const [uploadedFile, setUploadedFile] = useState()
  const [getUploadedFileLoading, setGetUploadedFileLoading] = useState(false)
  const [uploadError, setUploadError] = useState()
  const [noResponseError, setNoResponseError] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isDeletingLoading, setIsDeletingLoading] = useState(false)
  const [loadingProgressPercentage, setLoadingProgressPercentage] = useState(uploadedFile ? 100 : null)
  const inputFileRef = useRef()
  const onClickContainer = (e) => {
    if (uploadedFile) return
    if (noResponseError) return
    if (inputFileRef) {
      inputFileRef.current.click()
    }
    e.persist()
  }
  const onFileChange = async (e) => {
    const file = e.target.files[0]
    const { size: fileSize, type: fileType } = file
    setUploadError()
    if (fileType !== 'image/jpeg' && fileType !== 'image/png' && fileType !== 'application/pdf') {
      setUploadError('The format of the file you want to upload is not valid, please try again')
      if (inputFileRef.current.value) inputFileRef.current.value = ''
      return
    }

    if (fileSize >= 5000000) {
      setUploadError('The format of the file you want to upload is not valid, please try again')
      if (inputFileRef.current.value) inputFileRef.current.value = ''
      return
    }

    try {
      setLoadingProgressPercentage(0)
      const fileExtension = file.name.split('.').slice(-1)[0]
      const newFilename = `${receiptAttachmentType}.${fileExtension}`
      const [{ data: credentials }, fileBuffer] = await Promise.all([getAWSCredentials(), fileToBuffer(file)])
      await putObjectToS3(
        credentials,
        {
          Bucket: process.env.REACT_APP_BOOKING_ATTACHMENTS_BUCKET,
          Key: `${booking_id}/${newFilename}`,
          Body: fileBuffer,
          Metadata: {
            available_for: 'tenant',
            type: receiptAttachmentType,
            filename: newFilename,
          },
        },
        setLoadingProgressPercentage,
      )
      await getUploadedReceiptAfterUpload()
    } catch (err) {
      console.error(err)
    }
    setLoadingProgressPercentage()
    e.persist()
    e.preventDefault()
  }
  const getUploadedReceiptAfterUpload = async (times = 1) => {
    setGetUploadedFileLoading(true)
    try {
      const receiptAttachments = await getBookingAttachmentByType(booking_id, receiptAttachmentType)
      if (receiptAttachments?.length > 0) {
        setUploadedFile(receiptAttachments[0])
        setGetUploadedFileLoading(false)
      } else if (times > 5) {
        setGetUploadedFileLoading(false)
        setNoResponseError(true)
      } else {
        setTimeout(getUploadedReceiptAfterUpload.bind(null, times + 1), 2000)
      }
    } catch (error) {
      setGetUploadedFileLoading(false)
      console.log('[getUploadedReceipt] Error: ', error)
    }
  }
  const getUploadedReceipt = async () => {
    setGetUploadedFileLoading(true)
    try {
      const receiptAttachments = await getBookingAttachmentByType(booking_id, receiptAttachmentType)
      if (receiptAttachments?.length > 0) {
        setUploadedFile(receiptAttachments[0])
      }
    } catch (error) {
      console.log('[getUploadedReceipt] Error: ', error)
    }
    setGetUploadedFileLoading(false)
  }
  const handleDeleteReceipt = async () => {
    try {
      setIsDeletingLoading(true)
      await deleteBookingAttachment(uploadedFile?.id)
      setUploadedFile()
    } catch (error) {
      console.log('[handleDeleteReceipt] Error:', error)
    }
    setIsDeletingLoading(false)
    setIsDeleting(false)
  }
  const handleDownloadReceipt = async () => {
    try {
      const { data: response } = await getBookingAttachmentDownloadLink(booking_id, uploadedFile?.id)
      console.log('response.presigned_url: ', response)
      window.open(response.presigned_url)
    } catch (error) {
      console.log('[handleDownloadReceipt] Error:', error)
    }
  }
  const handleClose = () => {
    window.location.reload()
  }
  useEffect(() => {
    getUploadedReceipt()
  }, [])
  useEffect(() => {
    if (uploadedFile) {
      clearErrors('receipt is required')
      dispatch({ type: 'SET_IS_RECEIPT_FILE_UPLOADED', payload: true })
    } else {
      dispatch({ type: 'SET_IS_RECEIPT_FILE_UPLOADED', payload: false })
      setError('receipt is required', {
        type: 'manual',
        message: 'receipt is required',
      })
    }
    return () => {
      clearErrors('receipt is required')
    }
  }, [uploadedFile])
  return (
    <>
      {noResponseError ? (
        <>
          <Alert severity="error">{t('booking.paymentInfo.transfer.noResponseErrorUploadAlert')}</Alert>
          <Dialog open={true}>
            <DialogTitle
              style={{ backgroundColor: '#f44336', color: '#fff' }}
              id="no-response-upload-error"
            >
              <Warning style={{ color: 'error.main', marginRight: 5 }} />
              {t('booking.paymentInfo.transfer.noResponseErrorUploadTitle')}
            </DialogTitle>
            <DialogContent>
              <DialogContentText style={{ color: '#f44336' }} id="alert-dialog-description">
                {t('booking.paymentInfo.transfer.noResponseErrorUploadAlert')}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} style={{ color: '#f44336' }} autoFocus>
                {t('booking.paymentInfo.transfer.refreshButton')}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      ) : (
        <Box
          style={{
            cursor: uploadedFile ? null : 'pointer',
          }}
          className={classes.uploadContainer}
          onClick={onClickContainer}
        >
          <Box
            style={{
              width: `${loadingProgressPercentage ?? 0}%`,
            }}
            className={classes.uploadProgress}
          />
          <Box className={classes.inputContainer}>
            {getUploadedFileLoading && (
              <>
                <Box>{t('booking.paymentInfo.transfer.checkingReceipt')}</Box>
                <CircularProgress />
              </>
            )}
            {uploadedFile && (
              <>
                {!isDeleting && (
                  <>
                    <Box>{uploadedFile?.filename}</Box>
                    <Box className={classes.inputContainer}>
                      <Delete
                        onClick={setIsDeleting.bind(null, true)}
                        className={`${classes.iconCursor} ${classes.iconActionDanger}`}
                      />
                      <GetApp onClick={handleDownloadReceipt} className={`${classes.iconCursor} ${classes.iconActionPrimary}`} />
                    </Box>
                  </>
                )}
                {isDeleting &&
                  (isDeletingLoading ? (
                    <>
                      <Box>{uploadedFile?.filename}</Box>
                      <CircularProgress />
                    </>
                  ) : (
                    <>
                      <Box>{t('booking.paymentInfo.transfer.confirmDeleteReceipt')}</Box>
                      <Box className={classes.inputContainer}>
                        <DeleteForever
                          onClick={handleDeleteReceipt}
                          className={`${classes.iconCursor} ${classes.iconActionDanger}`}
                        />
                        <Close
                          onClick={setIsDeleting.bind(null, false)}
                          className={`${classes.iconCursor} ${classes.iconActionPrimary}`}
                        />
                      </Box>
                    </>
                  ))}
              </>
            )}
            {!uploadedFile && !getUploadedFileLoading && (
              <>
                <Box>{uploadError ?? t('booking.paymentInfo.transfer.receipt')}</Box>
                <input type="file" hidden ref={inputFileRef} onChange={onFileChange} />
                {loadingProgressPercentage ? (
                  <CircularProgress />
                ) : (
                  <Backup className={`${classes.iconCursor} ${classes.iconActionPrimary}`} />
                )}
              </>
            )}
          </Box>
        </Box>
      )}
    </>
  )
}

UploadReceipt.propTypes = {
  booking_id: PropTypes.string,
  setError: PropTypes.func,
  clearErrors: PropTypes.func,
}

export default UploadReceipt
